import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, Product, ModelType } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
// ApiService not used - direct fetch for dual camera support
import { useDetectionWebSocket } from "../../hooks/useWebSocket";
import { DETECTION_CLASS_COLORS, getDetectionColor, BBOX_STYLE } from "../../config/detectionColors";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const CheckingEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [cameraFrame, setCameraFrame] = useState<string>('');
  const [rightCameraFrame, setRightCameraFrame] = useState<string>(''); // Right camera frame
  const [cameraError, setCameraError] = useState<string>('');
  const [detectionError, setDetectionError] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [multiObjectDetections, setMultiObjectDetections] = useState<any[]>([]); // Store multi-object results
  const [rightCameraDetections, setRightCameraDetections] = useState<any[]>([]); // Right camera detections
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const detectionInProgressRef = useRef(false);
  const detectionLoopStartedRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rightCanvasRef = useRef<HTMLCanvasElement>(null); // Canvas for right camera

  // Debug panel state
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [detectionLoopStatus, setDetectionLoopStatus] = useState('not started');
  const [lastInferenceTime, setLastInferenceTime] = useState<number | null>(null);
  const [lastDetectionCount, setLastDetectionCount] = useState(0);

  // Determine if we should use multi-object detection based on selected model
  // All TREY models and Boots models use multi-object detection
  const useMultiObject = state.settings.selectedModel === ModelType.TREY_V3_SCAN ||
                         state.settings.selectedModel === ModelType.TREY_V4_SCAN ||
                         state.settings.selectedModel === ModelType.TREY_V41_SCAN ||
                         state.settings.selectedModel === ModelType.TREY_V42_SCAN ||
                         state.settings.selectedModel === ModelType.BOOTS_V42;
  
  // WebSocket for real-time detection
  const { 
    isConnected: wsConnected, 
    detectionStatus, 
    detectionResult, 
    startDetection 
  } = useDetectionWebSocket(state.settings.selectedModel);

  // Reset detection flag on component mount and cleanup on unmount
  useEffect(() => {
    detectionInProgressRef.current = false;
    detectionLoopStartedRef.current = false;
    hasNavigatedRef.current = false;
    // Clear any stale camera frame from previous session
    setCameraFrame('');
    setRightCameraFrame('');
    setMultiObjectDetections([]);
    setRightCameraDetections([]);
    setDetectionLoopStatus('not started');
    console.log('🔄 Component mounted, reset detection flags and cleared stale frames');

    return () => {
      // Cleanup intervals when component unmounts
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }

      // Stop any in-progress detection to release camera
      detectionInProgressRef.current = false;
      detectionLoopStartedRef.current = false;

      console.log('🛑 Component unmounted, cleaned up intervals and stopped detection');
    };
  }, []);

  // Load products from catalog based on selected model
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Select appropriate products file based on model
        // All TREY models and Boots models use chanel-demo-products.json
        const productsFile =
          state.settings.selectedModel === ModelType.TREY_V3_SCAN ||
          state.settings.selectedModel === ModelType.TREY_V4_SCAN ||
          state.settings.selectedModel === ModelType.TREY_V41_SCAN ||
          state.settings.selectedModel === ModelType.TREY_V42_SCAN ||
          state.settings.selectedModel === ModelType.BOOTS_V42
            ? '/chanel-demo-products.json'
            : '/mock-products.json';

        const response = await fetch(productsFile);
        const productsData = await response.json();
        setProducts(productsData);

        console.log(`📦 Loaded products from ${productsFile}:`, productsData.length, 'products');
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    loadProducts();
  }, [state.settings.selectedModel]);

  const handleCancel = () => {
    dispatch({ type: "SET_STEP", payload: 1 });
    navigate("/add-empty");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  // FAST camera preview loop (~10 FPS) - just grabs frames, no inference
  useEffect(() => {
    if (!useMultiObject) return;

    let frameRequestId = 0;

    const fetchCameraFrame = async () => {
      const thisRequestId = ++frameRequestId;

      try {
        // Fast camera-only endpoint on Jetson (~100ms)
        const response = await fetch(`http://192.168.55.1:8000/api/camera/preview?camera=0&t=${Date.now()}`);
        const data = await response.json();

        // Only update if this is the latest request
        if (thisRequestId !== frameRequestId) return;

        if (data.frame) {
          const frameData = data.frame.startsWith('data:image')
            ? data.frame
            : `data:image/jpeg;base64,${data.frame}`;
          setCameraFrame(frameData);
          setCameraError('');
        }
      } catch (error) {
        // Silent fail for preview - detection loop will show errors
      }
    };

    // Fast preview loop - fire every 150ms, allow overlapping (only latest used)
    fetchCameraFrame();
    const previewInterval = setInterval(fetchCameraFrame, 150);

    return () => {
      frameRequestId++;
      clearInterval(previewInterval);
    };
  }, [useMultiObject]);

  // SLOW detection loop (~0.2 FPS) - runs inference on Jetson
  useEffect(() => {
    if (!useMultiObject) {
      setMultiObjectDetections([]);
      setRightCameraDetections([]);
      return;
    }

    let detectionRequestId = 0;
    let consecutiveErrors = 0;
    const MAX_ERRORS = 5;
    let isDetecting = false;

    const fetchDetections = async () => {
      // Prevent overlapping detection requests (each takes ~5s)
      if (isDetecting) return;
      isDetecting = true;

      const thisRequestId = ++detectionRequestId;

      try {
        // Full detection endpoint on Jetson (~5s inference)
        const response = await fetch(`http://192.168.55.1:8000/api/detect/dual?model=${state.settings.selectedModel}&t=${Date.now()}`);
        const data = await response.json();

        // Only update if this is the latest request
        if (thisRequestId !== detectionRequestId) {
          isDetecting = false;
          return;
        }

        if (data.success) {
          consecutiveErrors = 0;

          // Process left camera detections
          if (data.left?.success && data.left.detections) {
            setMultiObjectDetections(data.left.detections);
            setLastDetectionCount(data.left.detections.length);
          } else {
            setMultiObjectDetections([]);
            setLastDetectionCount(0);
          }

          if (data.left?.detection_time) {
            setLastInferenceTime(data.left.detection_time * 1000);
          }

          // Process right camera detections
          if (data.right?.success && data.right.detections) {
            setRightCameraDetections(data.right.detections);
          } else {
            setRightCameraDetections([]);
          }
        } else {
          consecutiveErrors++;
          if (consecutiveErrors >= MAX_ERRORS) {
            setCameraError('Detection service unavailable');
          }
        }
      } catch (error) {
        if (thisRequestId !== detectionRequestId) {
          isDetecting = false;
          return;
        }
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_ERRORS) {
          setCameraError('Unable to connect to detection service');
        }
      }

      isDetecting = false;
    };

    // Detection loop - fires every 1s, but guard prevents overlapping
    fetchDetections();
    const detectionInterval = setInterval(fetchDetections, 1000);

    return () => {
      detectionRequestId++;
      clearInterval(detectionInterval);
    };
  }, [useMultiObject, state.settings.selectedModel]);

  // Helper function to draw bounding boxes on a canvas
  const drawBoundingBoxes = (
    canvas: HTMLCanvasElement | null,
    imgId: string,
    detections: any[]
  ) => {
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = document.getElementById(imgId) as HTMLImageElement;
    if (!img || !img.naturalWidth) return;

    // Set canvas size to match displayed image size
    const rect = img.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Only draw if we have detections
    if (detections.length === 0) return;

    // Draw bounding boxes using shared colors
    detections.forEach((det) => {
      const [x1, y1, x2, y2] = det.bbox;
      const color = getDetectionColor(det.class_name);

      // Scale bbox from natural image size to displayed size
      const scaleX = canvas.width / img.naturalWidth;
      const scaleY = canvas.height / img.naturalHeight;

      const scaledX1 = x1 * scaleX;
      const scaledY1 = y1 * scaleY;
      const scaledWidth = (x2 - x1) * scaleX;
      const scaledHeight = (y2 - y1) * scaleY;

      // Draw rectangle
      ctx.strokeStyle = color;
      ctx.lineWidth = BBOX_STYLE.lineWidth;
      ctx.strokeRect(scaledX1, scaledY1, scaledWidth, scaledHeight);

      // Draw label background
      const label = `${det.class_name} ${(det.confidence * 100).toFixed(0)}%`;
      ctx.font = BBOX_STYLE.labelFont;
      const textMetrics = ctx.measureText(label);

      ctx.fillStyle = color;
      ctx.fillRect(scaledX1, scaledY1 - BBOX_STYLE.labelHeight - 2, textMetrics.width + 6, BBOX_STYLE.labelHeight + 2);

      // Draw label text
      ctx.fillStyle = '#000000';
      ctx.fillText(label, scaledX1 + BBOX_STYLE.labelPadding, scaledY1 - 4);
    });
  };

  // Draw bounding boxes on both camera canvases
  useEffect(() => {
    if (!useMultiObject) return;

    // Draw on left camera
    drawBoundingBoxes(canvasRef.current, 'camera-image', multiObjectDetections);

    // Draw on right camera
    drawBoundingBoxes(rightCanvasRef.current, 'right-camera-image', rightCameraDetections);
  }, [multiObjectDetections, rightCameraDetections, useMultiObject, cameraFrame, rightCameraFrame]);

  // Navigate when multi-object detections are found (from left camera - primary)
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    // Reset navigation flag when component mounts
    hasNavigatedRef.current = false;
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Don't navigate if already navigated or not in multi-object mode
    if (hasNavigatedRef.current || !useMultiObject || multiObjectDetections.length === 0 || products.length === 0) {
      return;
    }

    console.log(`🔍 Checking ${multiObjectDetections.length} detections against ${products.length} products...`);

    // Map detections to products
    const detectedProducts = multiObjectDetections
      .map((det: any): Product | null => {
        const detectedName = det.class_name?.toLowerCase() || '';

        // Skip 'cosmetics' class
        if (detectedName === 'cosmetics') return null;

        // Class name to product ID mapping
        // Nespresso mapping: arpeggio/volluto -> Classic, stormio/altissio -> Vertuo
        const classToProductId: { [key: string]: string } = {
          'ceravefoamcleanser': 'cerave-foaming-cleanser-236ml',
          'ceravecleanser': 'cerave-foaming-cleanser-236ml',  // Boots v4.2 class name
          'chanelombreessentielle': 'chanel-ombre-essentielle',
          'maclipstick': 'mac-lipstick',
          'macmascara': 'mac-mascara',
          'niveacreme': 'nivea-cream-75ml',
          'tangleteezerpinkclassic': 'tangle-teezer-classic',
          // Nespresso Classic (small original line pods): Arpeggio, Volluto
          'nespressoarpeggio': 'nespresso-classic',
          'nespressovolluto': 'nespresso-classic',
          'nespressogoldpod': 'nespresso-classic',      // Legacy TREYv3 class
          'nespressopurplepod': 'nespresso-classic',    // Legacy TREYv3 class
          'coffee-pods': 'nespresso-classic',           // Generic fallback
          'nespressoclassic': 'nespresso-classic',      // Direct class name
          // Nespresso Vertuo (large vertuo line pods): Stormio, Altissio
          'nespressostormio': 'nespresso-vertuo',
          'nespressoaltissio': 'nespresso-vertuo',
          'nespressovertuo': 'nespresso-vertuo',        // Boots v4.2 class name
          'vertuo': 'nespresso-vertuo',                 // TREYv4.2 class name
          // Boots v4.2 specific classes
          'n7mascara': 'n7-mascara',
          'simplescrub': 'simple-scrub',
          '17powder': '17-powder'
        };

        const productId = classToProductId[detectedName];
        if (productId) {
          const matchedProduct = products.find(p => p.id === productId);
          if (matchedProduct) {
            console.log(`  ✅ Matched: ${detectedName} -> ${matchedProduct.name}`);
            return {
              ...matchedProduct,
              detectionConfidence: det.confidence || 0,
              detectionMethod: 'yolov7'
            } as Product;
          } else {
            console.log(`  ⚠️ Product ID ${productId} not found in catalog`);
          }
        } else {
          console.log(`  ❌ No mapping for: ${detectedName}`);
        }
        return null;
      })
      .filter((p: Product | null): p is Product => p !== null);

    // Navigate if we have matched products (with delay to show detections visually)
    if (detectedProducts.length > 0 && !hasNavigatedRef.current) {
      console.log(`🚀 Multi-object: Will navigate with ${detectedProducts.length} matched products in 1.7s`);
      hasNavigatedRef.current = true; // Prevent multiple navigations

      navigationTimeoutRef.current = setTimeout(() => {
        navigate('/multiple-items-detected', {
          state: { detections: multiObjectDetections, detectedProducts }
        });
      }, 1700); // 1.7 second delay to show bounding boxes
    }
  }, [multiObjectDetections, useMultiObject, products, navigate]);

  // Function to find matching product in catalog
  const findMatchingProduct = (detectionResult: any): Product | null => {
    if (!detectionResult || !products.length) return null;

    const detectedName = detectionResult.product_name?.toLowerCase() || '';
    const detectedSize = detectionResult.volume?.toLowerCase() || detectionResult.size?.toLowerCase() || '';

    // Try to match by cleaning up the detected name
    // Remove file extension if present
    const cleanedName = detectedName.replace(/\.(jpg|jpeg|png|webp)$/i, '').trim();

    console.log(`🔍 Trying to match: "${cleanedName}" (size: "${detectedSize}")`);
    console.log(`🎯 Current model: ${state.settings.selectedModel}`);

    // Special handling for Kiehl's products - always map to 125ml
    if (cleanedName.includes('kiehls') || cleanedName.includes("kiehl's") ||
        cleanedName.includes('ultra facial cream') || cleanedName.includes('ultra facial creme')) {
      console.log('🎯 Detected Kiehl\'s product - mapping to 125ml version');
      return products.find(p => p.id === 'kiehls-ultra-facial-cream-125ml') || null;
    }
    
    // Look for product that matches both name AND size
    const matchedProduct = products.find(product => {
      const productName = product.name.toLowerCase();
      const productBrand = product.brand?.toLowerCase() || '';
      const productSize = product.size.toLowerCase();
      
      // Match by name and/or brand
      const nameMatches = cleanedName.includes(productName) || 
                         productName.includes(cleanedName) ||
                         (productBrand && cleanedName.includes(productBrand)) ||
                         // Special case for variations
                         (cleanedName.includes('creme de corps') && productName.includes('creme de corps')) ||
                         // MAC products (brand-specific matching)
                         (cleanedName.includes('mac') && cleanedName.includes('mascara') && productBrand === 'mac') ||
                         (cleanedName.includes('mac') && cleanedName.includes('lipstick') && productBrand === 'mac') ||
                         // Other demo products
                         (cleanedName.includes('cerave') && productBrand === 'cerave') ||
                         (cleanedName.includes('nivea') && productBrand === 'nivea');
      
      // Match by size if we have it
      const sizeMatches = !detectedSize || productSize === detectedSize || 
                         productSize === detectedSize.replace(/\s/g, '');
      
      return nameMatches && sizeMatches;
    });
    
    return matchedProduct || null;
  };

  // Process detection results from WebSocket
  useEffect(() => {
    console.log('🔍 Detection result changed:', detectionResult);

    // Handle multi-object detection results (YOLOv7) from WebSocket
    if (useMultiObject && detectionResult) {
      console.log('🔍 Multi-object check - useMultiObject:', useMultiObject);
      console.log('🔍 detectionResult.success:', detectionResult.success, 'type:', typeof detectionResult.success);
      console.log('🔍 Full detectionResult:', JSON.stringify(detectionResult, null, 2));

      if (detectionResult.success) {
        // Clear any previous errors on successful detection
        setDetectionError('');

        const detections = (detectionResult as any).detections || [];
        console.log(`📦 Multi-object mode (WebSocket) - detected ${detections.length} items`);
        console.log('🔍 Products catalog loaded:', products.length, 'products');
        console.log('🔍 Detection class names:', detections.map((d: any) => d.class_name));

        if (detections.length > 0) {
        // Match each detection to products in catalog
        const detectedProducts: Product[] = detections
          .map((det: any): Product | null => {
            const detectedName = det.class_name?.toLowerCase() || '';
            console.log(`🔍 Trying to match detection: "${detectedName}"`);

            // Skip unused 'cosmetics' class (no training examples)
            if (detectedName === 'cosmetics') {
              console.log(`  ⏭️ Skipping unused class: "cosmetics"`);
              return null;
            }

            // YOLOv7 / TREYv3-SCAN / TREYv4-SCAN class name mapping to product IDs
            // Note: detectedName is lowercased, so all keys must be lowercase
            // Nespresso mapping: arpeggio/volluto -> Classic, stormio/altissio -> Vertuo
            const classToProductId: { [key: string]: string } = {
              // Common classes (both v3 and v4)
              'ceravefoamcleanser': 'cerave-foaming-cleanser-236ml',
              'chaneломbreessentielle': 'chanel-ombre-essentielle',
              'chanelombreessentielle': 'chanel-ombre-essentielle',
              'maclipstick': 'mac-lipstick',
              'macmascara': 'mac-mascara',
              'niveacreme': 'nivea-cream-75ml',
              'tangleteezerpinkclassic': 'tangle-teezer-classic',
              // Nespresso Classic (small original line pods): Arpeggio, Volluto
              'nespressoarpeggio': 'nespresso-classic',
              'nespressovolluto': 'nespresso-classic',
              'nespressogoldpod': 'nespresso-classic',      // Legacy TREYv3 class
              'nespressopurplepod': 'nespresso-classic',    // Legacy TREYv3 class
              'coffee-pods': 'nespresso-classic',           // Generic fallback
              // Nespresso Vertuo (large vertuo line pods): Stormio, Altissio
              'nespressostormio': 'nespresso-vertuo',
              'nespressoaltissio': 'nespresso-vertuo'
            };

            // Direct mapping by class name
            const productId = classToProductId[detectedName];
            if (productId) {
              const matchedProduct = products.find(p => p.id === productId);
              if (matchedProduct) {
                console.log(`  ✅ Matched "${detectedName}" to: ${matchedProduct.brand} ${matchedProduct.name}`);
                return {
                  ...matchedProduct,
                  detectionConfidence: det.confidence || 0,
                  detectionMethod: 'yolov7'
                } as Product;
              }
            }

            // Fallback: try fuzzy matching by brand name
            const matchedProduct = products.find(p => {
              if (!p.brand || !p.name) return false;
              const productBrand = p.brand.toLowerCase().replace(/\s+/g, '');

              // Check if class name starts with brand (e.g., "cerave" in "ceravefoamcleanser")
              const brandMatch = detectedName.startsWith(productBrand) ||
                                 detectedName.includes(productBrand);

              console.log(`  - Checking ${p.brand} ${p.name}: brandMatch=${brandMatch}`);
              return brandMatch;
            });

            if (matchedProduct) {
              console.log(`  ✅ Fallback matched to: ${matchedProduct.brand} ${matchedProduct.name}`);
              return {
                ...matchedProduct,
                detectionConfidence: det.confidence || 0,
                detectionMethod: 'yolov7'
              } as Product;
            }

            console.log(`  ❌ No match found for: ${detectedName}`);
            return null;
          })
          .filter((p: Product | null): p is Product => p !== null);

        console.log(`🎯 Total matched products: ${detectedProducts.length}`);
        if (detectedProducts.length > 0 && !hasNavigatedRef.current) {
          console.log(`✨ WebSocket: Will navigate to /multiple-items-detected with ${detectedProducts.length} products in 3s`);
          hasNavigatedRef.current = true;
          navigationTimeoutRef.current = setTimeout(() => {
            navigate('/multiple-items-detected', {
              state: { detections, detectedProducts }
            });
          }, 3000);
        } else if (detectedProducts.length === 0) {
          console.log('⚠️ No matching products found in catalog');
          setDetectionError('Detected items but could not match them to known products');
        }
        }
      } else {
        // Detection failed in multi-object mode - just log it, don't show error
        // (errors are transient and will auto-retry)
        console.log('⏭️ Multi-object detection attempt failed, will retry');
        setDetectionError(''); // Clear any previous errors
      }
      return;
    }

    if (detectionResult && detectionResult.success) {
      console.log('✅ Detection successful! Product:', detectionResult.product_name);

      // Check if empty tray was detected
      const detectedName = detectionResult.product_name?.toLowerCase() || '';
      const cleanedName = detectedName.replace(/\.(jpg|jpeg|png|webp)$/i, '').trim();

      if (cleanedName === 'empty_tray' || cleanedName === 'empty') {
        console.log('📭 Empty tray detected - showing custom message');
        setDetectionError("Place your product on the tray");
        // Continue detection loop - don't navigate away
        return;
      }

      // Try to find matching product in catalog
      const matchedProduct = findMatchingProduct(detectionResult);

      if (matchedProduct) {
        console.log('✨ Found matching product in catalog:', matchedProduct);

        // Get the measured weight from scale (accumulated weight = item weight only)
        const measuredWeight = detectionResult.detected_weight;
        const expectedWeight = matchedProduct.weight;
        const weightTolerance = matchedProduct.weightTolerance || 5; // Default 5g tolerance

        // WEIGHT VALIDATION - Check if measured weight matches expected product weight
        // Skip validation if weight monitoring is disabled
        if (!state.settings.disableWeightMonitoring && measuredWeight !== undefined && expectedWeight !== undefined) {
          const deviation = Math.abs(measuredWeight - expectedWeight);
          const weightValid = deviation <= weightTolerance;

          console.log('⚖️ Weight Validation:', {
            measured: measuredWeight,
            expected: expectedWeight,
            tolerance: weightTolerance,
            deviation: deviation,
            valid: weightValid
          });

          if (!weightValid) {
            // Weight doesn't match - show error and continue detection
            console.error('❌ Weight validation failed! Product weight does not match.');
            // HIDDEN FOR DEMO - Weight mismatch message
            // setDetectionError(
            //   `${matchedProduct.brand} ${matchedProduct.name}: Weight mismatch detected. Expected ${expectedWeight}g (±${weightTolerance}g), but measured ${measuredWeight}g. ` +
            //   `Please ensure the product is empty and placed correctly.`
            // );
            // Don't navigate - keep detecting
            // return;
          }

          console.log('✅ Weight validation passed!');
        } else {
          if (state.settings.disableWeightMonitoring) {
            console.log('⚖️ Weight validation skipped - disabled in settings');
          } else {
            console.warn('⚠️ Weight validation skipped - missing weight data');
          }
        }

        // Use the matched product from catalog
        const detectedProduct = {
          ...matchedProduct,
          detectionConfidence: detectionResult.confidence || 0,
          detectionMethod: detectionResult.detection_method || "unknown",
          detectedWeight: measuredWeight,
          expectedWeight: expectedWeight
        };

        console.log('📦 Using catalog product:', detectedProduct);

        // Add detected product to context - PREVIEW it first
        dispatch({ type: "PREVIEW_PRODUCT", payload: detectedProduct });
        dispatch({ type: "SET_STEP", payload: 5 });

        console.log('🚀 Navigating to /empty-accepted with product:', detectedProduct);
        // Navigate with the product data directly to ensure it's shown
        navigate("/empty-accepted", { state: { detectedProduct } });
      } else {
        // Fallback: create product from detection result
        console.log('⚠️ No matching product found in catalog, using detection data');

        const measuredWeight = detectionResult.detected_weight;
        const expectedWeight = detectionResult.expected_weight;
        const defaultTolerance = 5; // Default 5g tolerance for unknown products

        // WEIGHT VALIDATION - Even for unknown products, validate if we have expected weight
        // Skip validation if weight monitoring is disabled
        if (!state.settings.disableWeightMonitoring && measuredWeight !== undefined && expectedWeight !== undefined) {
          const deviation = Math.abs(measuredWeight - expectedWeight);
          const weightValid = deviation <= defaultTolerance;

          console.log('⚖️ Weight Validation (fallback):', {
            measured: measuredWeight,
            expected: expectedWeight,
            tolerance: defaultTolerance,
            deviation: deviation,
            valid: weightValid
          });

          if (!weightValid) {
            // Weight doesn't match - show error and continue detection
            console.error('❌ Weight validation failed! Product weight does not match.');
            // HIDDEN FOR DEMO - Weight mismatch message
            // const productLabel = detectionResult.product_name || "Product";
            // setDetectionError(
            //   `${productLabel}: Weight mismatch detected. Expected ${expectedWeight}g (±${defaultTolerance}g), but measured ${measuredWeight}g. ` +
            //   `Please ensure the product is empty and placed correctly.`
            // );
            // Don't navigate - keep detecting
            // return;
          }

          console.log('✅ Weight validation passed!');
        } else {
          if (state.settings.disableWeightMonitoring) {
            console.log('⚖️ Weight validation skipped - disabled in settings');
          } else {
            console.warn('⚠️ Weight validation skipped - missing weight data');
          }
        }

        const detectedProduct = {
          id: detectionResult.barcode || `detected-${Date.now()}`,
          name: detectionResult.product_name || "Unknown Product",
          brand: detectionResult.brand || "Kiehl's",
          category: "skincare",
          size: detectionResult.volume || "Standard",
          price: 25.00,
          image: detectionResult.image || "/api/placeholder/200/200",
          rewardValue: detectionResult.rewardValue || 10,
          isBranded: true,
          rewards: {
            loyaltyPoints: 50,
            dollarValue: "$5.00",
            discountValue: "20%"
          },
          detectionConfidence: detectionResult.confidence || 0,
          detectionMethod: detectionResult.detection_method || "unknown",
          detectedWeight: measuredWeight,
          expectedWeight: expectedWeight
        };

        console.log('📦 Created fallback product:', detectedProduct);

        // Only preview the product, don't add it to cart yet
        dispatch({ type: "PREVIEW_PRODUCT", payload: detectedProduct });
        dispatch({ type: "SET_STEP", payload: 5 });

        console.log('🚀 Navigating to /empty-accepted...');
        navigate("/empty-accepted");
      }
      
      // Show mock detection warning if using mock data
      if (detectionResult.using_mock) {
        console.warn("Using MOCK detection. For real detection, install dependencies.");
      }
    } else if (detectionResult && !detectionResult.success) {
      console.error('❌ Detection failed:', detectionResult.error);
      // Check if it's a "no item detected" scenario (HTTP 500 from empty scan)
      if (detectionResult.error && detectionResult.error.includes("HTTP Error 500")) {
        setDetectionError("No item detected. Please ensure your cosmetic's empty is placed in the scanning area. Need help? Speak to one of our experts.");
      } else {
        setDetectionError(detectionResult.error || "Unable to identify product. Please try again.");
      }
    } else {
      console.log('⏳ No detection result yet');
    }
  }, [detectionResult, products, dispatch, navigate, useMultiObject]);

  // NOTE: Navigation for multi-object detections is handled by the effect at line ~330
  // that monitors multiObjectDetections with hasNavigatedRef to prevent duplicate navigations

  // Start continuous detection when ready - only once
  useEffect(() => {
    const isCameraReady = !!cameraFrame && !cameraError;
    const isWebSocketReady = wsConnected;

    // Debug logging
    console.log('🔍 Detection readiness check:', {
      cameraFrame: !!cameraFrame,
      cameraError,
      wsConnected,
      isCameraReady,
      isWebSocketReady,
      bothReady: isCameraReady && isWebSocketReady,
      alreadyStarted: detectionLoopStartedRef.current
    });

    if (isCameraReady && isWebSocketReady && !detectionLoopStartedRef.current) {
      detectionLoopStartedRef.current = true;
      setDetectionLoopStatus('starting');
      console.log('🚀 Starting continuous detection loop (ONCE)');

      // Start detection loop after 2 second initial delay
      setTimeout(() => {
        console.log('⏰ Initial delay complete, starting detection attempts');
        setDetectionLoopStatus('running');

        // Trigger detection every 3 seconds (within our 2s rate limit with buffer)
        const tryDetection = () => {
          if (!detectionInProgressRef.current) {
            console.log('📸 Attempting detection...');
            startDetection();
          } else {
            console.log('⏭️ Skipping detection - previous detection still in progress');
          }
        };

        // First attempt
        tryDetection();

        // Then repeat every 3 seconds
        detectionIntervalRef.current = setInterval(tryDetection, 3000);
      }, 2000);
    } else if (!isCameraReady || !isWebSocketReady) {
      if (!isCameraReady) setDetectionLoopStatus('waiting for camera');
      else if (!isWebSocketReady) setDetectionLoopStatus('waiting for websocket');
    }
  }, [cameraFrame, cameraError, wsConnected, startDetection]);

  // Track detection progress
  useEffect(() => {
    if (detectionStatus === 'detecting') {
      detectionInProgressRef.current = true;
      console.log('🔄 Detection in progress...');
    } else if (detectionStatus === 'complete') {
      detectionInProgressRef.current = false;
      console.log('✅ Detection completed, ready for next attempt');
    }
  }, [detectionStatus]);

  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden">
      {/* Animated Teal Glow Effect at Bottom */}
      <motion.div
        className="absolute bottom-[-675px] left-0 w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
        style={{
          background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
          filter: 'blur(200px)',
        }}
        animate={{
          y: [0, -100, 0],
          opacity: [.25, .75, .25],
          scale: [1, 1.25, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">

        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
              showSettings={true}
            />
          </div>
        </div>

        {/* Main Content Area - with padding */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 relative mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading title={getCopy("checkingEmpty.heading")} />
              </motion.div>
            </div>
          </div>

          {/* Row 2 - Dual Camera Preview with Scanning Effects */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="relative"
              >
                {/* Model Indicator - Centered above cameras */}
                <div className="flex justify-center mb-4">
                  <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${useMultiObject ? 'bg-yellow-400' : 'bg-green-400'}`} />
                    <span className="text-white text-sm font-semibold">
                      {state.settings.selectedModel === ModelType.TREY_V42_SCAN ? 'TREYv4.2' :
                       state.settings.selectedModel === ModelType.TREY_V41_SCAN ? 'TREYv4.1' :
                       state.settings.selectedModel === ModelType.TREY_V4_SCAN ? 'TREYv4' :
                       state.settings.selectedModel === ModelType.TREY_V3_SCAN ? 'TREYv3' :
                       state.settings.selectedModel === ModelType.BOOTS_V42 ? 'Boots v4.2' :
                       'Unknown'}
                    </span>
                    <span className="text-gray-400 text-xs ml-2">
                      {lastInferenceTime !== null ? `${lastInferenceTime.toFixed(0)}ms` : ''}
                      {lastDetectionCount > 0 ? ` • ${lastDetectionCount} detected` : ''}
                    </span>
                  </div>
                </div>

                {/* Single Camera Preview (Left - Primary) */}
                <div className="flex justify-center">
                  <div className="w-full max-w-[600px] relative">
                    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-gray-900">
                      {cameraError ? (
                        <div className="w-full h-full bg-brand-surface flex items-center justify-center">
                          <p className="text-brand-text-secondary font-brand-secondary text-sm">{cameraError}</p>
                        </div>
                      ) : cameraFrame ? (
                        <>
                          <img
                            id="camera-image"
                            src={cameraFrame}
                            alt="Camera view"
                            className="w-full h-full object-cover"
                          />
                          {useMultiObject && (
                            <canvas
                              ref={canvasRef}
                              className="absolute top-0 left-0 w-full h-full pointer-events-none"
                            />
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full bg-brand-surface animate-pulse flex items-center justify-center">
                          <p className="text-brand-text-secondary font-brand-secondary text-sm">Loading...</p>
                        </div>
                      )}

                      {/* Scanning border effect */}
                      <motion.div
                        animate={{ opacity: [0.3, 0.7, 0.3] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 rounded-2xl pointer-events-none"
                        style={{ border: `3px solid ${brandConfig.colors.primary[isDarkMode ? 'dark' : 'light']}` }}
                      />

                      {/* Scanning line */}
                      <motion.div
                        className="absolute left-0 right-0 h-0.5 pointer-events-none"
                        style={{
                          background: `linear-gradient(to right, transparent, ${brandConfig.colors.primary[isDarkMode ? 'dark' : 'light']}, transparent)`,
                          filter: "blur(1px)",
                          opacity: 0.8,
                        }}
                        animate={{ top: ["10%", "90%", "10%"] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Debug Panel Toggle */}
                <button
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                  className="absolute top-12 right-4 z-20 bg-black/70 backdrop-blur-sm px-2 py-1 rounded text-white text-xs font-semibold hover:bg-black/80 transition-colors"
                >
                  {showDebugPanel ? '✕' : '🔧'}
                </button>

                {/* Debug Panel */}
                {showDebugPanel && (
                  <div className="absolute top-20 right-4 z-20 bg-black/90 backdrop-blur-sm px-3 py-2 rounded-lg text-white text-xs font-mono space-y-1 min-w-[200px]">
                    <div className="text-yellow-400 font-bold mb-1">Debug</div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Loop:</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${detectionLoopStatus === 'running' ? 'bg-green-600' : 'bg-gray-600'}`}>
                        {detectionLoopStatus}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">WebSocket:</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${wsConnected ? 'bg-green-600' : 'bg-red-600'}`}>
                        {wsConnected ? 'OK' : 'Down'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Camera:</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${cameraFrame ? 'bg-green-600' : 'bg-red-600'}`}>
                        {cameraFrame ? 'OK' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Detections:</span>
                      <span className="text-cyan-400">{multiObjectDetections.length}</span>
                    </div>
                  </div>
                )}

                {/* Error message or instructions */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="mt-8 space-y-4 text-center"
                >
                  <div className="font-brand-secondary font-light text-[28px] text-brand-text-secondary">
                    {getCopy("checkingEmpty.subheading")}
                    <span className="inline-flex ml-1">
                      <motion.span
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
                      >
                        .
                      </motion.span>
                      <motion.span
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                      >
                        .
                      </motion.span>
                      <motion.span
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
                      >
                        .
                      </motion.span>
                    </span>
                  </div>
                  {detectionError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-4 p-4 rounded-2xl ${
                        detectionError.includes("Place your product on the tray")
                          ? "bg-blue-50 text-blue-700"
                          : detectionError.includes("No item detected")
                          ? "bg-brand-surface text-brand-text-secondary"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      <p className="text-lg font-medium font-brand-secondary">
                        {detectionError}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-12 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex gap-4 justify-center">
            <Button
              variant="secondary"
              onClick={handleCancel}
            >
              {getCopy("checkingEmpty.buttons.secondary")}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default CheckingEmptyView;